import { ApolloClient, from, HttpLink, InMemoryCache, Operation } from '@apollo/client';
import { onError } from '@apollo/client/link/error';
import fetch from 'cross-fetch';
import { snackbarRef } from '../snackbar';
import { createNetworkStatusNotifier } from 'react-apollo-network-status';
import Router from 'next/router';

export type { ResultOf } from '@graphql-typed-document-node/core';

const wordEngineVersion = `0-17`;

export const wordEngineEndpoint = (path: `/${string}`) =>
    `api/wordengine/api/hub/v${wordEngineVersion}` + path;

const mutationsWithLocalErrorHandling = ['createEditWord'];

// get the operation name from the query as our zeus version doesn't assign operation.operationName
const getOperationName = (operation: Operation): string | null => {
    const definition = operation?.query?.definitions?.[0];
    if (definition && 'selectionSet' in definition) {
        const selection = definition.selectionSet?.selections?.[0];
        if (selection && 'name' in selection) {
            return selection.name?.value;
        }
    }
    return null;
};

// Log any GraphQL errors or network error that occurred
const errorLink = onError(({ graphQLErrors, networkError, operation }) => {
    const operationName = getOperationName(operation);

    if (
        mutationsWithLocalErrorHandling.includes(operationName) &&
        graphQLErrors.every((entry) => entry.message.includes('Edit word already exists'))
    ) {
        return;
    }
    if (graphQLErrors)
        graphQLErrors.forEach(({ message, locations, path }) => {
            console.log(
                `[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`
            );
            snackbarRef.enqueueSnackbar(
                `[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`,
                { variant: 'error' }
            );
            if (message.includes('jwt')) {
                Router.push('/login');
            }
        });
    if (networkError) {
        console.log(`[Network error]: ${networkError}`);
        snackbarRef.enqueueSnackbar(`[Network error]: ${networkError}`, { variant: 'error' });
    }
});

const batchLink = new HttpLink({
    uri: () => wordEngineEndpoint('/graphql'),
    credentials: 'include',
    fetch
});

const { link, useApolloNetworkStatus: networkStatus } = createNetworkStatusNotifier();

export const useApolloNetworkStatus = networkStatus;

const client = new ApolloClient({
    cache: new InMemoryCache({
        typePolicies: {
            Word: {
                keyFields: ['id', 'version']
            }
        }
    }),
    defaultOptions: {
        mutate: {
            errorPolicy: 'all'
        },
        query:
            typeof window === 'undefined'
                ? {
                      // disable cache serverside
                      fetchPolicy: 'no-cache',
                      errorPolicy: 'all'
                  }
                : undefined
    },
    link: from([link, errorLink, batchLink])
});

export default client;
